import { Signal, signal } from '@angular/core';

import {
  CellEditingStoppedEvent,
  GridApi,
  GridOptions,
  PostProcessPopupParams,
  RowClassParams,
  RowClickedEvent,
  RowNode,
} from 'ag-grid-enterprise';
import { BehaviorSubject, Subject, map } from 'rxjs';

import { CamAbstractInput } from '@camelot/form-model';

import { ActiveFilterTag } from '../../filters/types';
import { removeFilterCondition } from '../helpers/filter';
import { GridRow } from '../services/dto/grid-row';
import { GridMetaData } from '../services/dto/metadata';
import { CamGridFormService } from '../services/grid-form.service';
import { BaseCol, IColOptions } from './columns/base-col';
import { CamGridColumns, GRID_SELECTION_COL_ID } from './grid-columns';
import { CamGridContextMenu } from './grid-context-menu';
import { CamGridCursor } from './grid-cursor';
import { CamGridDataSource } from './grid-datasource';
import { CamGridFilters } from './grid-filters';
import { CamGridView } from './grid-view';
import { getTreeRowClasses } from './tree-row-classes';
import {
  ContentMenuAction,
  EditabledCellEdit,
  EditabledShortcutEvent,
  FilterModelSource,
  GridMutationResult,
  GridPaginationState,
  GridViewType,
  IContentMenuActionConfig,
  IDataService,
  IFieldsOverrides,
  IForcedFilter,
  IGridData,
  ITreeData,
} from './types';

export type GridContext = {
  cols: { [index: string]: BaseCol<any> };
  grid: CamGridData<any>;
};

export type GridClickableNode<T> = Pick<
  RowNode<T>,
  'data' | 'group' | 'isSelected' | 'rowIndex' | 'rowPinned' | 'selectable' | 'setSelectedParams'
>;

export class CamGridData<T extends { id: string }> implements IGridData<T> {
  public api: GridApi<T> | null = null;
  public ready$ = new BehaviorSubject(false);

  private readonly _ready = signal(false);
  private readonly _pagination = signal<GridPaginationState>({
    currentPage: 0,
    pageSize: 0,
    totalPages: 0,
    totalRows: 0,
  });

  /**
   * Signal, et non plus une simple méthode : AG Grid émet `onGridReady` en dehors du
   * contexte Angular. En mode zoneless, plus rien ne planifie de détection de changement,
   * donc un `@if (isReady)` évalué une fois à `false` ne serait jamais réévalué — la barre
   * d'outils de grille et le paginateur resteraient vides indéfiniment.
   * La signature reste appelable (`isReady()`), les appelants existants sont inchangés.
   */
  public readonly isReady = this._ready.asReadonly();

  /** Idem : les compteurs de pagination vivent dans l'API AG Grid, hors d'Angular. */
  public readonly pagination = this._pagination.asReadonly();

  private readonly _rowCount = signal(0);
  public readonly rowCount = this._rowCount.asReadonly();

  public cursor = new CamGridCursor();
  public filters: CamGridFilters<T> = new CamGridFilters(this);
  public columns: CamGridColumns<T> = new CamGridColumns(this);
  public contextMenu = new CamGridContextMenu<T>();
  public dataSource: CamGridDataSource<T> = new CamGridDataSource(this);
  public view: CamGridView<T> = new CamGridView(this);

  public clickedRow$ = new Subject<T>();
  public selectedData$ = new Subject<T[]>();
  public dataUpdated$ = new Subject<void>();
  public editabledData$ = new Subject<EditabledCellEdit<T>>();
  public expandedChanged$ = new Subject<boolean>();
  public showHiddenChanged$ = new Subject<boolean>();

  // Keyboard-shortcut channel, mirroring `contextMenuAction$`.
  public shortcutAction$ = new Subject<EditabledShortcutEvent>();
  // Published by the editabled grid so the shortcuts control can validate before emitting.
  public shortcutMenu: IContentMenuActionConfig[] = [];

  public filteredData$ = new BehaviorSubject<T[]>([]);

  get contextMenuAction$() {
    return this.contextMenu.contextMenuAction$;
  }

  get filteredDataLength$() {
    return this.filteredData$.pipe(map(list => list.length));
  }
  get isAtLeastOneNodeOpen() {
    if (!this.ready$.value) {
      return false;
    }
    let oneOpen = false;
    this.api?.forEachNode(node => {
      if (node.expanded) {
        oneOpen = true;
      }
    });
    return oneOpen;
  }

  public cols: { [index: string]: BaseCol<any> } = {};

  get displayType(): Signal<GridViewType> {
    return this.view.displayType;
  }

  get treeMode(): boolean {
    return this.api?.getGridOption('treeData') ?? false;
  }

  public isTreeData: boolean = false;
  public showHidden: boolean = false;
  public expanded: boolean = false;

  constructor(
    public scope: string,
    private _formService: CamGridFormService<T>
  ) {}

  public destroy() {
    this.api?.destroy();
    this.api = null;
    this.dataSource.clear();
    this._pagination.set({ currentPage: 0, pageSize: 0, totalPages: 0, totalRows: 0 });
    this._rowCount.set(0);
    this._setReady(false);
  }

  /** Recopie l'état de pagination d'AG Grid dans le signal, pour que la vue puisse le suivre. */
  public syncPagination() {
    if (!this.api) {
      return;
    }
    this._pagination.set({
      currentPage: this.api.paginationGetCurrentPage(),
      pageSize: this.api.paginationGetPageSize(),
      totalPages: this.api.paginationGetTotalPages(),
      totalRows: this.api.paginationGetRowCount(),
    });
  }

  public refreshServerSide() {
    this.api?.refreshServerSide({ purge: true });
  }

  public setApi(api: GridApi<T>) {
    this.api = api;
  }

  public setData(params: {
    colsMetaData?: GridMetaData[] | null;
    data: T[];
    fieldsOverrides?: IFieldsOverrides;
    forcedFilter?: IForcedFilter;
    options?: Omit<GridOptions, 'treeData'> & {
      colOptions?: IColOptions;
      contextMenuItems: ContentMenuAction[];
      openByDefault?: boolean;
      services?: IDataService<T> | null;
      treeData?: ITreeData<T> | null;
    };
    showHierarchyLabel?: boolean;
  }) {
    if (!this.api) {
      return;
    }
    this.filters.forcedFilter = params.forcedFilter;

    this.api.setGridOption('context', { cols: this.cols, grid: this });

    if (params.options?.pagination) {
      this.api.setGridOption('pagination', true);

      this.api.setGridOption('paginationPageSize', params.options?.paginationPageSize ?? 10);
      this.api.setGridOption('cacheBlockSize', params.options?.cacheBlockSize ?? 5);

      this.api.setGridOption('onPaginationChanged', page => {
        // Toujours synchroniser d'abord : c'est le seul moment où l'on apprend le nombre
        // total de lignes, y compris quand la grille tient sur une page unique.
        this.syncPagination();

        if (!this.isReady() || page.api.paginationGetTotalPages() <= 1) {
          return;
        }
        this.filters.paginationStorage = page.api.paginationGetCurrentPage();
      });
    }

    this.dataSource.setup(params);

    const cols = params.colsMetaData ?? this.columns.generateColsMetaData(params.data, params.fieldsOverrides ?? {});
    this.columns.createColsModel(cols, params.fieldsOverrides ?? {}, {
      ...{ dataSearch$: params.options?.services?.getFieldsData$ },
      ...(params.options?.colOptions ?? {}),
    });
    this.api.setGridOption(
      'columnDefs',
      this.columns.createFields(
        params.fieldsOverrides,
        params.options?.rowSelection === 'multiple',
        params.showHierarchyLabel
      )
    );

    this.api.setGridOption('rowSelection', params.options?.rowSelection ?? 'single');

    // Tree-hierarchy row classes shared by both grids, merged with any caller `getRowClass`.
    const userGetRowClass = params.options?.getRowClass;
    const isTreeGrid = !!params.options?.treeData;
    this.api.setGridOption('getRowClass', (rowParams: RowClassParams<T>) => {
      const data = rowParams.data as unknown as GridRow | undefined;
      const classes = isTreeGrid ? getTreeRowClasses(data, !!data && this.dataSource.isLastChild(data.id)) : [];
      const userClasses = userGetRowClass?.(rowParams);
      if (userClasses) {
        classes.push(...(Array.isArray(userClasses) ? userClasses : [userClasses]));
      }
      return classes.length > 0 ? classes : undefined;
    });

    this.contextMenu.setup(this.api, params.options?.contextMenuItems);

    this.api.setGridOption('onRowClicked', (event: RowClickedEvent) => this._onClickedRow(event));
    this.api.setGridOption('onRowSelected', (event: RowClickedEvent) => this._onSelectionChanged(event));
    this.api.setGridOption('enterNavigatesVertically', true);
    this.api.setGridOption('enterNavigatesVerticallyAfterEdit', true);
    this.api.setGridOption('onCellEditingStopped', (event: CellEditingStoppedEvent<T>) => this._onEditabledData(event));
    this.api.setGridOption('postProcessPopup', (p: PostProcessPopupParams) => this._postProcessPopup(p));

    this.api.setGridOption('onFilterChanged', () => {
      this.filters.paginationStorage = 0;
      this.api?.paginationGoToPage(0);
      this.filters.filtersChanged$.next(Date.now());
      this.filters.changedAt.set(Date.now());
    });
    this.api.setGridOption('onModelUpdated', ({ api }) => this._rowCount.set(api.getDisplayedRowCount()));

    if (params.options?.domLayout) {
      this.api.setGridOption('domLayout', params.options.domLayout);
    }

    if (params.options?.isFullWidthRow) {
      this.api.setGridOption('isFullWidthRow', params.options.isFullWidthRow);
    }
    if (params.options?.getRowHeight) {
      this.api.setGridOption('getRowHeight', params.options.getRowHeight);
    }
    if (params.options?.fullWidthCellRenderer) {
      this.api.setGridOption('fullWidthCellRenderer', params.options.fullWidthCellRenderer);
    }

    if (this.filters.filterModelSource) {
      this.filters.applyFilterModel(this.filters.filterModelSource, FilterModelSource.storage);
    }

    this.syncPagination();
    this._setReady(true);
  }

  public updateData(data: T[]) {
    this.dataSource.setData(data);
  }

  public updateRows(result: GridMutationResult<T>) {
    if (!this.api) {
      return;
    }
    this.dataSource.updateRows(result);
    this.dataUpdated$.next();
  }

  public deselectAll() {
    this.api?.deselectAll();
  }

  /**
   * Clic sur une ligne rendue hors d'AG Grid (vue tableau `cam-grid-table`) : reproduit le clic
   * natif — curseur, `clickedRow$`, puis la sélection AG Grid (`rowClicked`) avec Ctrl / Cmd
   * (ajout / retrait) et Shift (plage), selon `rowSelection`, `suppressRowClickSelection`,
   * `rowMultiSelectWithClick` et `suppressRowDeselection`.
   */
  public clickRow(node: GridClickableNode<T>, event?: MouseEvent) {
    const api = this.api;
    if (!node.data || !api) {
      return;
    }
    this.cursor.setCurrent(node.rowIndex ?? 0, node.data.id);
    this.clickedRow$.next(node.data);

    const selectionBlocked =
      !node.selectable ||
      !!node.rowPinned ||
      !api.getGridOption('rowSelection') ||
      api.getGridOption('suppressRowClickSelection') ||
      (api.getGridOption('groupSelectsChildren') && node.group);
    if (selectionBlocked) {
      return;
    }

    const isMultiKey = event?.ctrlKey || event?.metaKey || false;
    const isShiftKey = event?.shiftKey ?? false;
    const multiSelectOnClick = api.getGridOption('rowMultiSelectWithClick') ?? false;
    const rowDeselectionWithCtrl = !api.getGridOption('suppressRowDeselection');
    const source = 'rowClicked';

    if (!node.isSelected()) {
      node.setSelectedParams({
        clearSelection: multiSelectOnClick ? false : !isMultiKey,
        event,
        newValue: true,
        rangeSelect: isShiftKey,
        source,
      });
      return;
    }
    if (multiSelectOnClick || (isMultiKey && rowDeselectionWithCtrl)) {
      node.setSelectedParams({ event, newValue: false, source });
      return;
    }
    if (!isMultiKey) {
      node.setSelectedParams({
        clearSelection: !isShiftKey,
        event,
        newValue: true,
        rangeSelect: isShiftKey,
        source,
      });
    }
  }

  public toggleShowHidden() {
    if (!this.api) {
      return;
    }
    this.showHidden = !this.showHidden;
    this.showHiddenChanged$.next(this.showHidden);
  }

  public toggleExpanded(value?: boolean) {
    this.expanded = value ?? !this.expanded;
    this.expandedChanged$.next(this.expanded);
  }

  public switchViewDefault(type: GridViewType) {
    this.view.switchViewDefault(type);
  }

  public switchView(type: GridViewType, save = true) {
    this.view.switchView(type, save);
  }

  public switchTreeMode(value?: boolean) {
    this.view.switchTreeMode(value);
  }

  public getColumns() {
    return (
      this.api?.getColumns()?.filter(c => !c.getColDef().checkboxSelection && c.getColId() !== GRID_SELECTION_COL_ID) ??
      null
    );
  }

  public getTags(): { key: string; values: ActiveFilterTag[] }[] {
    const filters = this.api?.getFilterModel();

    if (!filters) {
      return [];
    }
    return Object.keys(filters)
      .map(key => ({
        key,
        values: this.filters.formatFilter(key),
      }))
      .filter(f => f.values.length > 0);
  }

  public removeTag(tag: ActiveFilterTag) {
    const model = this.api?.getFilterModel();
    if (!model) {
      return;
    }
    model[tag.id] = removeFilterCondition(model[tag.id], tag.condition);

    this.filters.applyFilterModel({ filterModel: JSON.stringify(model) }, FilterModelSource.storage);
    this.api?.onFilterChanged();
  }

  public getFiltersForm(): CamAbstractInput[] {
    return this._formService.getFiltersForm(this);
  }

  public getHighlightFiltersForm(): CamAbstractInput[] {
    return this._formService.getHighlightFiltersForm(this);
  }

  /** Garde le signal et l'observable historique alignés — `ready$` a encore des abonnés. */
  private _setReady(value: boolean) {
    this._ready.set(value);
    this.ready$.next(value);
  }

  private _onClickedRow(event: RowClickedEvent) {
    if (!event.data) {
      return;
    }
    this.cursor.setCurrent(event.rowIndex ?? 0, event.data.id);
    this.clickedRow$.next(event.data);
  }

  private _onSelectionChanged(_event: RowClickedEvent) {
    this.selectedData$.next(this.api?.getSelectedRows() ?? []);
  }

  private _onEditabledData(event: CellEditingStoppedEvent<T>) {
    const field = event.colDef.field;
    // un champ pointe (`a.b`) n'est pas une propriete de la ligne : on ne l'envoie pas
    if (!event.data || !field || field.includes('.') || !event.valueChanged) {
      return;
    }
    const id = event.data.id;
    this.editabledData$.next({
      changes: { [field]: event.newValue, id } as EditabledCellEdit<T>['changes'],
      previous: { [field]: event.oldValue, id } as EditabledCellEdit<T>['previous'],
    });
  }

  private _postProcessPopup(params: PostProcessPopupParams) {
    if (params.type !== 'columnMenu' || !params.column || !params.eventSource) {
      return;
    }

    const popup = params.ePopup;
    const popupParent = popup.parentElement;
    if (!popupParent) {
      return;
    }

    const parentRect = popupParent.getBoundingClientRect();
    const colRect = params.eventSource.getBoundingClientRect();
    const headerRow = params.eventSource.closest('.ag-header-row');
    if (!headerRow) {
      return;
    }

    const headerRect = headerRow.getBoundingClientRect();
    const popupWidth = popup.offsetWidth;

    const top = headerRect.bottom - parentRect.top;

    // Try to position to the right of the column, fallback to left if no space
    let left = colRect.right - parentRect.left;
    if (left + popupWidth > parentRect.width) {
      left = colRect.left - parentRect.left - popupWidth;
    }
    left = Math.max(0, Math.min(left, parentRect.width - popupWidth));

    popup.style.top = `${top}px`;
    popup.style.left = `${left}px`;
  }
}
